import { useState, useEffect, useRef } from "react";
import { useData } from "./contexts/DataContext";
import ReactGA from "react-ga4";

const DealsCarousel = ({
  adapter,
  isDbQuerying = true
}) => {
  const transitionTime = 250;
  const wrapAroundTransitionTime = transitionTime / 2;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [dealsData, setDealsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const carouselRef = useRef(null);
  const [itemsPerPage, setItemsPerPage] = useState(() =>
    window.innerWidth > 1152 ? 3 : 2
  );
  // Add new state variables for dragging
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  // Add a state to track if a significant drag occurred
  const [hasSignificantDrag, setHasSignificantDrag] = useState(false);

  // Add states for smooth wrap-around effect
  const [isWrappingAround, setIsWrappingAround] = useState(false);
  const [wrapDirection, setWrapDirection] = useState(null); // 'left' or 'right'
  const [wrapPhase, setWrapPhase] = useState(0); // 0: initial, 1: scroll out, 2: jump, 3: scroll in
  const [wrapTargetIndex, setWrapTargetIndex] = useState(null);
  const [transitionEnabled, setTransitionEnabled] = useState(true);

  // Timeout ref to clean up any pending transitions
  const wrapTimeoutRef = useRef(null);
  const handleNextRef = useRef(null);

  useEffect(() => {
    const updateItemsPerPage = () => {
      const nextItemsPerPage = window.innerWidth > 1152 ? 3 : 2;
      setItemsPerPage(nextItemsPerPage);
      setCurrentIndex(0);
    };

    window.addEventListener("resize", updateItemsPerPage);
    return () => window.removeEventListener("resize", updateItemsPerPage);
  }, []);

  const {
    apiEndpoints,
    dataLoaded,
    categoryData,
    cachedImageFailed,
    markCachedImageFailed,
    markImageLoaded,
    imageLoaded,
  } = useData();

  // Use the page-provided adapter to create deals for livestock or products.
  useEffect(() => {
    if (!adapter || !adapter.isReady({ dataLoaded, isDbQuerying })) {
      setIsLoading(true);
      return;
    }

    setIsLoading(true);
    const processDealsData = () => {
      try {
        setDealsData(adapter.getDeals());
        setCurrentIndex(0);
        setIsLoading(false);
      } catch (error) {
        console.error("Error processing deals data:", error);
        setIsLoading(false);
      }
    };

    processDealsData();
  }, [adapter, dataLoaded, isDbQuerying]);

  // Shared function for handling next/previous with wrap-around support
  const goToIndex = (index) => {
    // Check if we need to wrap around
    if (index < 0) {
      performWrapAround(dealsData.length - 1, "right");
      return;
    } else if (index >= dealsData.length) {
      performWrapAround(0, "left");
      return;
    }

    // Normal case - just set the index
    setCurrentIndex(index);
  };

  const handlePrevious = () => {
    const prevIndex = currentIndex - 1;
    goToIndex(prevIndex);
  };

  const handleNext = () => {
    const nextIndex = currentIndex + 1;
    goToIndex(nextIndex);
  };
  handleNextRef.current = handleNext;

  const handleDealClick = (deal) => {
    // Only process click if no significant drag occurred
    if (hasSignificantDrag) return;

    const event = adapter?.getAnalyticsEvent?.(deal);
    if (event) {
      ReactGA.event(event);
    }

    adapter?.onSelectDeal?.(deal);
  };

  // Auto-advance the carousel every 5 seconds
  useEffect(() => {
    if (dealsData.length <= itemsPerPage) return;

    const interval = setInterval(() => {
      if (isDragging) return; // Don't auto-advance while dragging
      handleNextRef.current?.();
    }, 5000);

    return () => clearInterval(interval);
  }, [dealsData.length, currentIndex, isDragging, itemsPerPage]);

  // Calculate the current translation offset
  const calculateTranslationForIndex = (index, dragOffset) => {
    const currentElement = carouselRef.current?.querySelector(`#deal-${index}`);
    const offsetLeft = currentElement?.offsetLeft || 0;
    return -offsetLeft + dragOffset;
  };

  // Calculate the current translation offset with wrap-around phases
  const calculateTranslation = () => {
    if (isLoading || dealsData.length === 0) return 0;

    // Handle different phases of wrap-around
    if (isWrappingAround) {
      const screenWidth = window.innerWidth;

      const direction = wrapDirection === "right" ? 1 : -1;
      if (wrapPhase === 1) {
        // Phase 1: Scroll the current item out of view
        return (
          calculateTranslationForIndex(currentIndex, 0) +
          screenWidth * 1 * direction
        );
      } else if (wrapPhase === 2) {
        // Phase 2: Instant jump to the opposite end (no transition)
        return (
          calculateTranslationForIndex(wrapTargetIndex, 0) -
          screenWidth * 1 * direction
        );
      }
      // Phase 3: Scroll to the target item (transition will be re-enabled)
      return calculateTranslationForIndex(wrapTargetIndex, 0);
    }

    // Normal case - just calculate translation based on current index and drag
    return calculateTranslationForIndex(currentIndex, dragOffset);
  };

  // Mouse event handlers for dragging
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStartX(e.clientX);
    // Reset the significant drag flag on mouse down
    setHasSignificantDrag(false);
    // Prevent text selection during dragging
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const currentX = e.clientX;
    const diff = currentX - dragStartX;
    setDragOffset(diff);

    // If the drag distance is significant, mark it so we can prevent click
    if (Math.abs(diff) > 10) {
      // Using a smaller threshold for detecting drag vs click
      setHasSignificantDrag(true);
    }
  };

  const handleMouseUp = () => {
    if (!isDragging) return;

    // Use the shared function to calculate the closest index based on the current translation
    const currentTranslation = calculateTranslationForIndex(
      currentIndex,
      dragOffset
    );
    let closestIndex = currentIndex;
    let closestDistance = Infinity;

    // Check if the drag is significant enough to possibly wrap around
    const significantDragRight = dragOffset > 150 && currentIndex === 0;
    const significantDragLeft =
      dragOffset < -150 && currentIndex === dealsData.length - 1;

    dealsData.forEach((_, index) => {
      const elementTranslation = calculateTranslationForIndex(index, 0);
      let distance = Math.abs(currentTranslation - elementTranslation);

      // If dragging right at the first item, consider wrapping to the end
      if (significantDragRight && index === dealsData.length - 1) {
        // Make the last item seem closer to encourage the wrap-around
        distance = distance * 0.5;
      }
      // If dragging left at the last item, consider wrapping to the beginning
      else if (significantDragLeft && index === 0) {
        // Make the first item seem closer to encourage the wrap-around
        distance = distance * 0.5;
      }

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    // Handle wrap-around for stronger drags
    if (dragOffset > 150 && currentIndex === 0 && dealsData.length > 1) {
      performWrapAround(dealsData.length - 1, "right");
    } else if (
      dragOffset < -150 &&
      currentIndex === dealsData.length - 1 &&
      dealsData.length > 1
    ) {
      performWrapAround(0, "left");
    } else {
      // Normal case - use the calculated closest index
      setCurrentIndex(closestIndex);
    }

    // Reset dragging state
    setIsDragging(false);
    setDragOffset(0);

    // We need to keep the hasSignificantDrag flag until the click event processes
    // It will be reset on the next mouseDown event
  };

  // Handle wrap-around transition in phases
  const performWrapAround = (targetIndex, direction) => {
    // Clear any existing timeout to prevent conflicts
    if (wrapTimeoutRef.current) {
      clearTimeout(wrapTimeoutRef.current);
    }

    // Start wrap-around sequence
    setIsWrappingAround(true);
    setWrapDirection(direction);
    setWrapTargetIndex(targetIndex);
    setWrapPhase(1); // Start with scroll out phase

    // Phase 1: Scroll current item out of view (with transition)
    setTransitionEnabled(true);

    // Phase 2: After scrolling out, disable transition and jump to other end
    wrapTimeoutRef.current = setTimeout(() => {
      setTransitionEnabled(false);
      setWrapPhase(2);

      // Phase 3: Re-enable transition and scroll to the target item
      wrapTimeoutRef.current = setTimeout(() => {
        setTransitionEnabled(true);
        setWrapPhase(3);
        setCurrentIndex(targetIndex);

        // Reset wrap-around state after animation completes
        wrapTimeoutRef.current = setTimeout(() => {
          setIsWrappingAround(false);
          setWrapDirection(null);
          setWrapPhase(0);
          setWrapTargetIndex(null);
        }, wrapAroundTransitionTime); // Half the transition duration
      }, 50); // Small delay to ensure no transition is visible
    }, wrapAroundTransitionTime); // Half the transition duration
  };

  // Touch event handlers for mobile
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setDragStartX(e.touches[0].clientX);
    setHasSignificantDrag(false);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const currentX = e.touches[0].clientX;
    const diff = currentX - dragStartX;
    setDragOffset(diff * 2); // Adjust sensitivity for touch

    // Mark as significant drag for touch too
    if (Math.abs(diff) > 10) {
      setHasSignificantDrag(true);
    }
  };

  const handleTouchEnd = () => {
    handleMouseUp();
  };

  // Add event listener cleanup
  useEffect(() => {
    const cleanup = () => {
      setIsDragging(false);
      setDragOffset(0);
      // We don't reset hasSignificantDrag here because we need it for the click handler
    };

    window.addEventListener("mouseup", cleanup);
    window.addEventListener("touchend", cleanup, { passive: false });

    return () => {
      window.removeEventListener("mouseup", cleanup);
      window.removeEventListener("touchend", cleanup);
      window.addEventListener("touchmove", cleanup);
    };
  }, []);

  // Clean up any pending timeouts
  useEffect(() => {
    return () => {
      if (wrapTimeoutRef.current) {
        clearTimeout(wrapTimeoutRef.current);
      }
    };
  }, []);

  const carouselContext = {
    apiEndpoints,
    categoryData,
    cachedImageFailed,
    markCachedImageFailed,
    markImageLoaded,
    imageLoaded,
  };

  const renderCarouselDeal = (deal, index) => {
    const key = adapter?.getDealKey?.(deal, index) || index;
    const posthogId = adapter?.posthogId || "deal-carousel-deal";
    const dealLabel =
      deal.common_name || deal.display_name || deal.label || "deal";
    const isMobile = window.innerWidth < 768;
    const imageProps = adapter?.getDealImageProps?.({
      deal,
      index,
      context: carouselContext,
      isMobile,
    });
    const shouldRenderImage = adapter?.shouldRenderDealImage
      ? adapter.shouldRenderDealImage({
          deal,
          index,
          context: carouselContext,
          isMobile,
        })
      : Boolean(imageProps?.src);
    const imageClassName = [
      "h-full w-full object-cover",
      adapter?.getDealImageClassName?.({
        deal,
        index,
        context: carouselContext,
        isMobile,
      }) || "",
    ].filter(Boolean).join(" ");
    const baseImageProps = { ...(imageProps || {}) };
    const imageAlt = baseImageProps.alt || "";
    delete baseImageProps.alt;
    delete baseImageProps.className;

    return (
      <div
        id={"deal-" + index}
        key={key}
        data-posthog={posthogId}
        className="deal-carousel-item flex-shrink-0 carousel-item"
      >
        <div
          className="deal-carousel-image-card relative cursor-pointer overflow-hidden rounded-lg bg-slate-800 shadow-lg"
          onClick={() => handleDealClick(deal)}
          onKeyDown={(event) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            handleDealClick(deal);
          }}
          role="button"
          tabIndex={0}
          aria-label={`View deal for ${dealLabel}`}
        >
          {shouldRenderImage && imageProps && (
            <div className="deal-carousel-image absolute inset-0">
              <img
                {...baseImageProps}
                alt={imageAlt}
                className={imageClassName}
              />
            </div>
          )}
          <div className="deal-carousel-overlay pointer-events-none absolute inset-0 z-10 flex flex-col justify-end p-4 text-white">
            {adapter.renderDealText({
              deal,
              index,
              context: carouselContext,
              isMobile,
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mb-4 overflow-hidden relative">
      {/* Updating overlay - shown when database is querying but deals are already displayed */}
      {dealsData.length > 0 && isDbQuerying && (
        <div className="absolute inset-0 z-20 flex items-start justify-center rounded-lg pointer-events-none">
          <div className="bg-white px-3 py-1.5 rounded-lg shadow-lg flex items-center gap-2 mt-4">
            <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
            <span className="text-blue-800 font-medium">Updating deals...</span>
          </div>
        </div>
      )}
      
      <div
        ref={carouselRef}
        className="flex flex-row items-start justify-start"
        style={{
          transform: `translateX(${calculateTranslation()}px)`,
          cursor: isDragging ? "grabbing" : "grab",
          transition: !transitionEnabled
            ? "none"
            : isDragging
            ? "none"
            : "transform ease-in-out " +
              (isWrappingAround ? wrapAroundTransitionTime : transitionTime) +
              "ms",
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="flex space-x-3 transition-transform duration-500 ease-in-out w-full">
          {dealsData.length === 0 ? (
            isLoading || isDbQuerying ? (
              <div
                className={
                  "p-3 bg-blue-50 rounded-lg animate-pulse w-full" +
                  (window.innerWidth < 768 ? " h-80" : "")
                }
              >
                <div
                  className={
                    "flex items-center justify-center" +
                    (window.innerWidth < 768 ? " h-full" : " h-48")
                  }
                >
                  <p className="text-blue-800 text-base text-center">
                    Loading deals...
                  </p>
                </div>
              </div>
            ) : (
              <div
                className={
                  "p-3 bg-blue-50 rounded-lg w-full" +
                  (window.innerWidth < 768 ? " h-80" : "")
                }
              >
                <div
                  className={
                    "flex flex-col items-center justify-center" +
                    (window.innerWidth < 768 ? " h-full" : " h-48")
                  }
                >
                  <p className="text-blue-800 text-base text-center">
                    No deals match your current filters.
                  </p>
                  <p>Try adjusting your filters to see more deals.</p>
                </div>
              </div>
            )
          ) : (
            dealsData.map(renderCarouselDeal)
          )}
        </div>
      </div>

      <div className="flex justify-center mt-2 gap-1">
        <button
          onClick={handlePrevious}
          className="bg-blue-800 bg-opacity-50 p-2 rounded-full text-white hover:bg-opacity-75 pointer-events-auto h-12"
          aria-label="Previous deal"
          data-posthog="deal-carousel-previous"
        >
          &#10094;
        </button>
        {Array.from({ length: Math.ceil(dealsData.length / itemsPerPage) }).map(
          (_, index) => {
            const pageStart = index * itemsPerPage;
            const isActive =
              currentIndex >= pageStart &&
              currentIndex < pageStart + itemsPerPage;
            return (
              <button
                key={index}
                className={`h-12 rounded-full transition-all ${
                  isActive ? "w-12 bg-blue-600" : "w-6 bg-gray-300"
                }`}
                onClick={() => setCurrentIndex(pageStart)}
                aria-label={`Go to page ${index + 1}`}
                data-posthog="deal-carousel-page"
                data-posthog-page={index + 1}
              />
            );
          }
        )}
        <button
          onClick={handleNext}
          className="bg-blue-800 bg-opacity-50 p-2 rounded-full text-white hover:bg-opacity-75 pointer-events-auto h-12"
          aria-label="Next deal"
          data-posthog="deal-carousel-next"
        >
          &#10095;
        </button>
      </div>
    </div>
  );
};

export default DealsCarousel;
